import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

export default function OrderTrackingApp() {
  const [currentView, setCurrentView] = useState('orders'); // 'orders' or 'details'
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    status: 'all',
    timeFrame: 'all'
  });
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });

  // Sample orders data
  const orders = [
    {
      id: 'OD43369703042455100',
      image: '/api/placeholder/80/80',
      title: 'realme Buds T110 (RMA2306) with AI ENC for calls, 38 hour...',
      deliveryDate: 'Feb 22',
      status: 'Pending Payment',
      price: '₹1,118',
      color: 'Black',
      seller: 'MPDSLERetail',
      returnPolicyEnd: 'Mar 01',
      orderConfirmed: 'Feb 21',
      orderDate: '13.05.2024',
      shippingMethod: 'standard / free',
      paymentMethod: 'credit card',
      recipient: 'Maria',
      country: 'Australia',
      address: '117292, Moscow, Vavilova street 39A/15'
    },
    {
      id: 'OD43369705678455123',
      image: '/api/placeholder/80/80',
      title: 'realme Buds 2 Neo With HD Mic ...',
      deliveryDate: 'May 10, 2024',
      status: 'Processing',
      price: '₹899',
      orderDate: '10.05.2024',
      shippingMethod: 'express / paid',
      paymentMethod: 'pending',
      recipient: 'John',
      country: 'India',
      address: 'Chennai, Tamil Nadu'
    },
    {
      id: 'OD43369708796455234',
      image: '/api/placeholder/80/80',
      title: 'EZGER Back Camera Lens Glass ...',
      status: 'Pending Payment',
      price: '₹399',
      orderDate: '05.05.2024',
      shippingMethod: 'standard / free',
      paymentMethod: 'pending',
      recipient: 'Dharshini',
      country: 'India',
      address: 'Bangalore, Karnataka'
    },
    {
      id: 'OD43369702341455675',
      image: '/api/placeholder/80/80',
      title: 'boAt BassHeads 220 Wired Hea...',
      deliveryDate: 'Jul 01, 2022',
      status: 'Delivered',
      price: '₹649',
      orderDate: '25.06.2022',
      shippingMethod: 'standard / free',
      paymentMethod: 'cash on delivery',
      recipient: 'Raj',
      country: 'India',
      address: 'Mumbai, Maharashtra'
    },
    {
      id: 'OD43369701112455987',
      image: '/api/placeholder/80/80',
      title: 'Gaana Plus Subscription - ...',
      deliveryDate: 'Jul 01, 2022',
      status: 'Delivered',
      price: 'Free',
      orderDate: '25.06.2022',
      shippingMethod: 'digital delivery',
      paymentMethod: 'credit card',
      recipient: 'Self',
      country: 'India',
      address: 'Digital Delivery'
    }
  ];

  const orderDetails = {
    items: [
      { image: '/api/placeholder/80/80', title: 'SHOPUMORE ...', price: '₹258', originalPrice: '₹799', discount: '67% off', assured: true },
      { image: '/api/placeholder/80/80', title: 'MUUCHSTAC ...', price: '₹220', originalPrice: '₹599', discount: '63% off' },
      { image: '/api/placeholder/80/80', title: 'StyleOx...', price: '₹271', originalPrice: '₹699', assured: true }
    ],
    pricing: {
      listPrice: '₹2,999',
      sellingPrice: '₹2,999',
      extraDiscount: '- ₹1,900',
      specialPrice: '₹1,099',
      deliveryCharge: '₹49 Free',
      handlingFee: '₹10',
      protectPromiseFee: '₹9',
      totalAmount: '₹1,118',
      paymentMethod: 'Cash On Delivery: ₹1118.0'
    }
  };

  // Add responsive window resize handler
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter orders based on search query and filter options
  // FIX: The issue was here - adding 'orders' to dependencies created an infinite loop
  // because we're referencing 'orders' inside the function but it's a constant that doesn't change
  useEffect(() => {
    let filtered = orders;
    
    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(order => 
        order.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply status filter
    if (filterOptions.status !== 'all') {
      filtered = filtered.filter(order => order.status === filterOptions.status);
    }
    
    // Apply time frame filter
    if (filterOptions.timeFrame !== 'all') {
      const currentDate = new Date();
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(currentDate.getMonth() - 3);
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(currentDate.getMonth() - 6);
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(currentDate.getFullYear() - 1);
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.orderDate);
        switch(filterOptions.timeFrame) {
          case '3months':
            return orderDate > threeMonthsAgo;
          case '6months':
            return orderDate > sixMonthsAgo;
          case '1year':
            return orderDate > oneYearAgo;
          default:
            return true;
        }
      });
    }
    
    setFilteredOrders(filtered);
  }, [searchQuery, filterOptions]); // Removed 'orders' from dependencies since it's static

  // Initialize filtered orders when component mounts
  useEffect(() => {
    setFilteredOrders(orders);
  }, []);

  // Handle order selection
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setCurrentView('details');
  };

  // Handle back button
  const handleBackClick = () => {
    setCurrentView('orders');
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle filter toggle
  const toggleFilterMenu = () => {
    setShowFilterMenu(!showFilterMenu);
  };

  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilterOptions({
      ...filterOptions,
      [type]: value
    });
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    setShowCancelPopup(true);
  };

  // Handle payment button click
  const handlePaymentClick = () => {
    setShowPaymentPopup(true);
  };

  // Handle cancel reason input
  const handleCancelReasonChange = (e) => {
    setCancelReason(e.target.value);
  };

  // Handle payment form input
  const handlePaymentInfoChange = (e) => {
    const { name, value } = e.target;
    setPaymentInfo({
      ...paymentInfo,
      [name]: value
    });
  };

  // Handle cancel submission
  const handleCancelSubmit = () => {
    if (!cancelReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }
    
    alert(`Order ${selectedOrder.id} has been cancelled.\nReason: ${cancelReason}`);
    setShowCancelPopup(false);
    setCancelReason('');
    
    // In a real implementation, you would update the order status
    // For now, we'll just go back to the orders list
    setCurrentView('orders');
  };

  // Handle payment submission
  const handlePaymentSubmit = () => {
    Navigate("./Thankpage.jsx")
  
    
    // In a real implementation, you would update the order status
    // For now, we'll just go back to the orders list
    setCurrentView('orders');
  };

  // Determine if desktop view
  const isDesktop = windowWidth >= 768;

  // Function to determine if order can be cancelled or needs payment
  const isOrderPendingPayment = (order) => {
    return order.status === 'Pending Payment' || order.paymentMethod === 'pending';
  };

  const isOrderCancellable = (order) => {
    return order.status !== 'Delivered' && order.status !== 'Cancelled';
  };

  return (
    <div className={`mx-auto bg-gray-100 min-h-screen ${isDesktop ? 'max-w-6xl' : 'max-w-md'}`}>
      {/* Header */}
      <div className="bg-white p-4 flex items-center justify-between border-b">
        <div className="flex items-center">
          <button onClick={handleBackClick} className="mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <h1 className="text-xl font-bold">
            {currentView === 'orders' ? 'My Orders' : 'Order Details'}
          </h1>
        </div>
      </div>

      {/* Content */}
      {currentView === 'orders' ? (
        <>
          {/* Search and Filter Bar */}
          <div className="p-4">
            <div className="bg-white rounded flex items-center justify-between p-4 shadow-sm">
              <div className="flex items-center w-full">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-400 mr-2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input 
                  type="text" 
                  placeholder="Search your order here" 
                  className="w-full outline-none"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="flex items-center ml-4 relative">
                <button onClick={toggleFilterMenu} className="flex items-center">
                  <span className="font-medium">Filters</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 ml-2">
                    <line x1="21" x2="3" y1="6" y2="6" />
                    <line x1="17" x2="7" y1="12" y2="12" />
                    <line x1="13" x2="11" y1="18" y2="18" />
                  </svg>
                </button>
                
                {showFilterMenu && (
                  <div className="absolute top-full right-0 mt-2 bg-white shadow-lg rounded-md p-4 z-10 w-64">
                    <h3 className="font-medium mb-2">Status</h3>
                    <div className="space-y-2 mb-4">
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="status" 
                          value="all" 
                          checked={filterOptions.status === 'all'} 
                          onChange={() => handleFilterChange('status', 'all')}
                          className="mr-2"
                        />
                        All
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="status" 
                          value="Delivered" 
                          checked={filterOptions.status === 'Delivered'} 
                          onChange={() => handleFilterChange('status', 'Delivered')}
                          className="mr-2"
                        />
                        Delivered
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="status" 
                          value="Pending Payment" 
                          checked={filterOptions.status === 'Pending Payment'} 
                          onChange={() => handleFilterChange('status', 'Pending Payment')}
                          className="mr-2"
                        />
                        Pending Payment
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="status" 
                          value="Processing" 
                          checked={filterOptions.status === 'Processing'} 
                          onChange={() => handleFilterChange('status', 'Processing')}
                          className="mr-2"
                        />
                        Processing
                      </label>
                    </div>
                    
                    <h3 className="font-medium mb-2">Time Period</h3>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="timeFrame" 
                          value="all" 
                          checked={filterOptions.timeFrame === 'all'} 
                          onChange={() => handleFilterChange('timeFrame', 'all')}
                          className="mr-2"
                        />
                        All Time
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="timeFrame" 
                          value="3months" 
                          checked={filterOptions.timeFrame === '3months'} 
                          onChange={() => handleFilterChange('timeFrame', '3months')}
                          className="mr-2"
                        />
                        Last 3 Months
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="timeFrame" 
                          value="6months" 
                          checked={filterOptions.timeFrame === '6months'} 
                          onChange={() => handleFilterChange('timeFrame', '6months')}
                          className="mr-2"
                        />
                        Last 6 Months
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          name="timeFrame" 
                          value="1year" 
                          checked={filterOptions.timeFrame === '1year'} 
                          onChange={() => handleFilterChange('timeFrame', '1year')}
                          className="mr-2"
                        />
                        Last 1 Year
                      </label>
                    </div>
                    
                    <div className="flex justify-end mt-4">
                      <button 
                        onClick={toggleFilterMenu}
                        className="bg-blue-500 text-white px-4 py-2 rounded-md"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order List */}
          <div className={`px-4 pb-20 ${isDesktop ? 'grid grid-cols-2 gap-4' : ''}`}>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <div key={order.id} className="bg-white mb-4 p-4 shadow-sm hover:shadow-md cursor-pointer transition-shadow" onClick={() => handleOrderClick(order)}>
                  <div className="flex items-start">
                    <img src={order.image} alt={order.title} className="w-16 h-16 mr-4 rounded" />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div className="text-sm text-gray-500">{order.status} {order.deliveryDate ? `on ${order.deliveryDate}` : ''}</div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-gray-400">
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </div>
                      <h3 className="text-sm font-medium mt-1 line-clamp-1">{order.title}</h3>
                      <p className="text-sm mt-1">Order Date: {order.orderDate}</p>
                      <p className="text-sm font-medium mt-1">{order.price}</p>
                      
                      {order.sharedBy && (
                        <div className="mt-2">
                          <span className="text-xs bg-gray-100 rounded-full px-3 py-1">Shared by {order.sharedBy}</span>
                        </div>
                      )}
                      
                      {isOrderPendingPayment(order) && (
                        <div className="mt-2">
                          <span className="text-xs bg-yellow-100 text-yellow-800 rounded-full px-3 py-1">Payment Required</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white p-8 text-center shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-gray-400">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <p className="text-lg font-medium">No orders found</p>
                <p className="text-gray-500 mt-2">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Order Details View */}
          {selectedOrder && (
            <div className={`bg-white ${isDesktop ? 'grid grid-cols-2 gap-4' : ''}`}>
              <div className="p-4 border-b">
                <div className="text-xs text-gray-500">Order ID - {selectedOrder.id}</div>
                
                <div className="flex mt-4">
                  <img src={selectedOrder.image} alt={selectedOrder.title} className="w-16 h-16 mr-4" />
                  <div>
                    <h3 className="font-medium">{selectedOrder.title}</h3>
                    <p className="text-sm text-gray-500">{selectedOrder.color}</p>
                    <div className="flex items-center mt-1">
                      <p className="text-sm">Seller: {selectedOrder.seller}</p>
                    </div>
                    <p className="font-medium mt-1">{selectedOrder.price}</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-3 mt-4 text-sm">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2 text-gray-500">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <path d="M14 2v6h6" />
                      <path d="M16 13H8" />
                      <path d="M16 17H8" />
                      <path d="M10 9H8" />
                    </svg>
                    <span>Status: {selectedOrder.status}</span>
                  </div>
                </div>

                

                {selectedOrder.status === 'Delivered' && (
                  <div className="mt-4 text-sm text-gray-500">
                    Return policy ended on {selectedOrder.returnPolicyEnd}
                  </div>
                )}
              </div>

              <div className={`p-4 border-b ${isDesktop ? 'col-span-2' : ''}`}>
                <h2 className="font-bold text-lg mb-4">Order Information</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium">Order Date</p>
                    <p className="text-gray-700">{selectedOrder.orderDate}</p>
                  </div>
                  <div>
                    <p className="font-medium">Shipping Method</p>
                    <p className="text-gray-700">{selectedOrder.shippingMethod}</p>
                  </div>
                  <div>
                    <p className="font-medium">Payment Method</p>
                    <p className="text-gray-700">{selectedOrder.paymentMethod}</p>
                  </div>
                </div>
              </div>

              <div className={`p-4 border-b ${isDesktop ? 'col-span-2' : ''}`}>
                <h2 className="font-bold text-lg mb-4">Shipping Information</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium">Recipient</p>
                    <p className="text-gray-700">{selectedOrder.recipient}</p>
                  </div>
                  <div>
                    <p className="font-medium">Country</p>
                    <p className="text-gray-700">{selectedOrder.country}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="font-medium">Address</p>
                    <p className="text-gray-700">{selectedOrder.address}</p>
                  </div>
                </div>
              </div>

              <div className={`p-4 ${isDesktop ? 'col-span-2' : ''}`}>
                <div className="flex justify-between gap-4">
                  {isOrderCancellable(selectedOrder) && (
                    <button 
                      onClick={handleCancelClick}
                      className="bg-red-500 text-white px-4 py-2 rounded-md flex items-center justify-center w-full"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                      Cancel Order
                    </button>
                  )}
                  
                  {isOrderPendingPayment(selectedOrder) && (
                    <button 
                      onClick={handlePaymentClick}
                      className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center justify-center w-full"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2">
                        <rect width="20" height="14" x="2" y="5" rx="2" />
                        <line x1="2" x2="22" y1="10" y2="10" />
                      </svg>
                      Make Payment
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Cancel Order Popup */}
      {showCancelPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Cancel Order</h2>
            <p className="mb-4">Are you sure you want to cancel order {selectedOrder.id}?</p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Please tell us why you're cancelling this order:
              </label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows="4"
                value={cancelReason}
                onChange={handleCancelReasonChange}
                placeholder="Enter your reason for cancellation"
              ></textarea>
            </div>
            
            <div className="flex justify-end space-x-4">
              <button 
                onClick={() => setShowCancelPopup(false)}
                className="px-4 py-2 border rounded-md"
              >
                Go Back
              </button>
              <button 
                onClick={handleCancelSubmit}
                className="bg-red-500 text-white px-4 py-2 rounded-md"
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Popup */}
      {showPaymentPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Make Payment</h2>
            <p className="mb-4">Complete payment for order {selectedOrder.id}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Card Number</label>
                <input
                  type="text"
                  name="cardNumber"
                  className="w-full p-2 border rounded-md"
                  placeholder="1234 5678 9012 3456"
                  value={paymentInfo.cardNumber}
                  onChange={handlePaymentInfoChange}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Cardholder Name</label>
                <input
                  type="text"
                  name="cardName"
                  className="w-full p-2 border rounded-md"
                  placeholder="John Doe"
                  value={paymentInfo.cardName}
                  onChange={handlePaymentInfoChange}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Expiry Date</label>
                  <input
                    type="text"
                    name="expiryDate"
                    className="w-full p-2 border rounded-md"
                    placeholder="MM/YY"
                    value={paymentInfo.expiryDate}
                    onChange={handlePaymentInfoChange}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">CVV</label>
                  <input
                    type="text"
                    name="cvv"
                    className="w-full p-2 border rounded-md"
                    placeholder="123"
                    value={paymentInfo.cvv}
                    onChange={handlePaymentInfoChange}
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-4">
              <button 
                onClick={() => setShowPaymentPopup(false)}
                className="px-4 py-2 border rounded-md"
              >
                Cancel
              </button>
              <button 
                onClick={handlePaymentSubmit}
                className="bg-green-500 text-white px-4 py-2 rounded-md"
              >
                Pay {selectedOrder ? selectedOrder.price : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation - Mobile Only */}
      {!isDesktop && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t flex justify-around py-3">
          <button className="flex flex-col items-center text-blue-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            <span className="text-xs mt-1">Home</span>
          </button>
          <button className="flex flex-col items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <circle cx="8" cy="21" r="1"/>
              <circle cx="19" cy="21" r="1"/>
              <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
            </svg>
            <span className="text-xs mt-1">Cart</span>
          </button>
          <button className="flex flex-col items-center text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
            </svg>
            <span className="text-xs mt-1">Wishlist</span>
          </button>
          <button className="flex flex-col items-center text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <span className="text-xs mt-1">Account</span>
          </button>
        </div>
      )}
    </div>
  );
}